import { useEffect, useRef, useState } from "react";
import {
  Collapse,
  Space,
  Tag,
  Card,
  Empty,
  Modal,
  Button,
  message,
  Input,
  Spin,
  Select,
  Table,
  FloatButton,
  InputNumber,
  Form,
  Divider,
  notification,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  UserAddOutlined,
  CopyOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import axios from "axios";
import {
  BASE_URL_BEN,
  GET_GEO_ALL,
  HEADERS_EXPORT,
  HEADERS_EXPORT_BEN,
  POST_EDIT_ASSET,
  POST_LOAD_MEMO_NEW,
  POST_UPDATE_MEMO,
} from "../../../API/apiUrls";
import AddCustomerRelation from "./AddCustomerRelation";
import TokenCheck from "../../../../hook/TokenCheck";
import { TYPE_OPTIONS } from "../constant/landType";

// ======================== Consts ========================

const fullName = (p) => [p?.SNAM, p?.NAME1, p?.NAME2].filter(Boolean).join(" ");

// ======================== Key Generators ========================
const rand = () => Math.random().toString(36).slice(2, 11);
const ts = () => Date.now();

const generatePersonKey = () => `person-${ts()}-${rand()}`;
const generateAssetKey = () => `assetLand-${ts()}-${rand()}`;

// ======================== Ensure keys on incoming data ========================
// Inject key ให้ทุก person/assetLand ทันทีตั้งแต่รับ data (เพื่อความเสถียรของ React)
const ensureKeysForData = (arr = []) =>
  (arr || []).map((person) => {
    const personWithKey = {
      ...person,
      key: person?.key || generatePersonKey(),
      assetLand: (person?.assetLand || []).map((a) => ({
        ...a,
        key: a?.key || generateAssetKey(),
      })),
    };
    return personWithKey;
  });

// ======================== Component ========================
const EditRelationAssets = ({ open, close, data = [], func, namePage }) => {
  const userCompany = localStorage.getItem("COMPANY_ID");
  const [loading, setLoading] = useState(false);
  const [selectedMap, setSelectedMap] = useState({});
  const [preData, setPreData] = useState(ensureKeysForData(data));
  const [changedKeys, setChangedKeys] = useState(new Set()); // เก็บ person.key
  const [changedKeysRelation, setChangedKeysRelation] = useState(new Set()); // เก็บ person.key
  const panelMapRef = useRef({});
  const scrollRef = useRef(null);
  const [signOut] = TokenCheck();
  const [geoRawData, setGeoRawData] = useState();
  const [activeKey, setActiveKey] = useState(); // เก็บเป็น person.key
  const [isModalAddCustomerRelation, setIsModalAddCustomerRelation] =
    useState(false);
  const [memoData, setMemoData] = useState();
  const [memoEditData, setMemoEditData] = useState();
  const USERNAME = localStorage.getItem("USERNAME");
  const [sumMoney, setSumMoney] = useState({});

  const toggleItem = (k) =>
    setActiveKey((prev) => (prev === k ? undefined : k));

  // ======================== Load geo ========================
  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);

      try {
        const [geoRes, memoRes] = await Promise.all([
          axios.get(GET_GEO_ALL, {
            headers: HEADERS_EXPORT_BEN,
          }),
          data && data.length > 0 && data[0]?.contno
            ? axios.post(POST_LOAD_MEMO_NEW, { contractNo: data[0].contno })
            : Promise.resolve({ data: null }),
        ]);

        setGeoRawData(geoRes.data);
        if (memoRes?.data) {
          setMemoData(memoRes?.data);
          console.log(memoRes?.data);
        }
      } catch (error) {
        if (error?.response?.status === 401) {
          signOut();
        }
        message.error(`ไม่พบข้อมูล: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, [data]);

  // ======================== Sort + inject keys when data prop changes ========================
  useEffect(() => {
    console.log("2");
    const withKeys = ensureKeysForData(data);
    setPreData(
      [...withKeys].sort((a, b) => {
        const aHasErrors = a.errors?.length > 0;
        const bHasErrors = b.errors?.length > 0;
        if (aHasErrors && !bHasErrors) return -1;
        if (!aHasErrors && bHasErrors) return 1;
        return 0;
      }),
    );
  }, [data]);

  // ======================== Update panelMapRef when preData changes ========================
  useEffect(() => {
    console.log("3");
    panelMapRef.current = {};
    preData.forEach((item) => {
      // ใช้ key เป็นหลัก
      panelMapRef.current[item.key] = item;
    });
  }, [preData]);

  useEffect(() => {
    const totals = preData.reduce(
      (acc, item) => {
        const m = item.money;
        return {
          tr14Sum: acc.tr14Sum + (Number(m?.tr14) || 0),
          familyRegistrationSum:
            acc.familyRegistrationSum + (Number(m?.familyRegistration) || 0),
          operationFeeSum: acc.operationFeeSum + (Number(m?.operationFee) || 0),
          spouseFeeSum: acc.spouseFeeSum + (Number(m?.spouseFee) || 0),
          additionalFeeSum:
            acc.additionalFeeSum + (Number(m?.additionalFee) || 0),
          totalSum: acc.totalSum + (Number(m?.total) || 0),
        };
      },
      {
        tr14Sum: 0,
        familyRegistrationSum: 0,
        operationFeeSum: 0,
        spouseFeeSum: 0,
        additionalFeeSum: 0,
        totalSum: 0,
      },
    );

    setSumMoney(totals);
  }, [preData]);

  // ======================== Asset patch helpers ========================
  const patchAssetRow = (row, patch) => ({ ...row, ...patch });

  const handleAssetPatch = (itemKey, rowIndex, patch) => {
    const owner = panelMapRef.current[itemKey];
    if (!owner) {
      console.warn("Owner not found for itemKey:", itemKey);
      return;
    }

    setPreData((prev) =>
      prev.map((itm) => {
        const isSameItem = itm.key === owner.key;
        if (!isSameItem) return itm;

        const nextAsset = (itm.assetLand || []).map((r, i) =>
          i === rowIndex ? patchAssetRow(r, patch) : r,
        );
        return { ...itm, assetLand: nextAsset };
      }),
    );
    setChangedKeys((prev) => new Set([...prev, owner.key]));
  };

  // ======================== Add Asset ========================
  const handleAddAsset = (itemKey) => {
    console.log("handleAddAsset", itemKey);

    const owner = panelMapRef.current[itemKey];
    if (!owner) {
      message.error("ไม่พบข้อมูลเจ้าของ");
      return;
    }

    const newAsset = {
      key: generateAssetKey(),
      landId: 0,
      assetOnlineId: owner.assetOnlineId || null,
      userId: null,
      typeLand: 1,
      numberLand: "",
      numberLandlawang: null,
      province: "",
      district: "",
      subdistrict: null,
      zipcode: null,
      landPrice: null,
      active: true,
      isNew: true,
    };

    setMemoEditData();
    setPreData((prev) =>
      prev.map((item) => {
        const isSameItem = item.key === owner.key;

        if (!isSameItem) return item;

        return {
          ...item,
          assetLand: [...(item.assetLand || []), newAsset],
          investigation: true,
        };
      }),
    );

    setChangedKeys((prev) => new Set([...prev, owner.key]));
    message.info("กรุณาใส่รายละเอียดทรัพย์");
  };

  // ======================== Columns ========================
  const makeAssetColumns = (itemKey) => [
    {
      title: "ประเภท",
      dataIndex: "typeLand",
      width: 120,
      render: (val, record, rowIndex) => (
        <Select
          size="small"
          style={{ width: "100%" }}
          options={TYPE_OPTIONS}
          value={val}
          onChange={(v) => handleAssetPatch(itemKey, rowIndex, { typeLand: v })}
          onMouseDown={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      title: "เลขที่ดิน",
      dataIndex: "numberLand",
      width: 160,
      render: (val, record, rowIndex) => (
        <Input
          size="small"
          value={val}
          onChange={(e) =>
            handleAssetPatch(itemKey, rowIndex, { numberLand: e.target.value })
          }
          onMouseDown={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      title: "จังหวัด",
      dataIndex: "province",
      width: 200,
      render: (val, record, rowIndex) => {
        const provOpts =
          geoRawData?.province?.map((p) => ({
            label: `${p.provCode} - ${p.provDesc}`,
            value: String(p.provCode),
            name: p.provDesc,
          })) || [];

        return (
          <Select
            showSearch
            size="small"
            optionFilterProp="label"
            style={{ width: "100%" }}
            value={record.province || undefined}
            options={provOpts}
            onChange={(provCode, opt) => {
              handleAssetPatch(itemKey, rowIndex, {
                province: provCode,
                provinceName: opt?.name || null,
                district: null,
                districtName: null,
                subdistrict: null,
                subdistrictName: null,
              });
            }}
            onMouseDown={(e) => e.stopPropagation()}
          />
        );
      },
    },
    {
      title: "เขต/อำเภอ",
      dataIndex: "district",
      width: 220,
      render: (val, record, rowIndex) => {
        const pvCode = record.province ? String(record.province) : null;
        const districtOpts = pvCode
          ? geoRawData?.district
              ?.filter((d) => String(d.provCode) === pvCode)
              .map((d) => ({
                label: `${d.distCode} - ${d.distDesc}`,
                value: String(d.distCode),
                name: d.distDesc,
              }))
          : [];

        return (
          <Select
            showSearch
            size="small"
            optionFilterProp="label"
            style={{ width: "100%" }}
            value={val || undefined}
            options={districtOpts}
            onChange={(distCode, opt) => {
              handleAssetPatch(itemKey, rowIndex, {
                district: String(distCode),
                districtName: opt?.name || null,
                subdistrict: null,
                subdistrictName: null,
              });
            }}
            disabled={!pvCode}
            onMouseDown={(e) => e.stopPropagation()}
          />
        );
      },
    },
    {
      title: "แขวง/ตำบล",
      dataIndex: "subdistrict",
      width: 240,
      render: (val, record, rowIndex) => {
        const pvCode = record.province ? String(record.province) : null;
        const dtCode = record.district ? String(record.district) : null;

        const subOpts =
          pvCode && dtCode
            ? geoRawData?.subDistrict
                ?.filter(
                  (s) =>
                    String(s.provCode) === pvCode &&
                    String(s.distCode) === dtCode,
                )
                .map((s) => ({
                  label: `${s.subDistCode} - ${s.subDistDesc}`,
                  value: String(s.subDistCode),
                  name: s.subDistDesc,
                }))
            : [];

        return (
          <Select
            showSearch
            size="small"
            optionFilterProp="label"
            style={{ width: "100%" }}
            value={val || undefined}
            options={subOpts}
            onChange={(subCode, opt) => {
              handleAssetPatch(itemKey, rowIndex, {
                subdistrict: String(subCode),
                subdistrictName: opt?.name || null,
              });
            }}
            disabled={!pvCode || !dtCode}
            onMouseDown={(e) => e.stopPropagation()}
          />
        );
      },
    },
    {
      title: "การจัดการ",
      width: 80,
      align: "center",
      render: (_, record, rowIndex) => (
        <Space size="small">
          {/* ปุ่มลบ/คืนค่า */}
          <Button
            type="primary"
            ghost
            danger={record.active}
            icon={record.active ? <DeleteOutlined /> : <UndoOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteAsset(itemKey, rowIndex);
            }}
          >
            {/* {record.active ? "ลบ" : "คืนค่า"} */}
          </Button>

          {/* ปุ่มคัดลอก */}

          <Button
            type="default"
            icon={<CopyOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleCopyAsset(itemKey, rowIndex);
            }}
          >
            {/* คัดลอก */}
          </Button>
        </Space>
      ),
    },
  ];

  const handleDeleteAsset = (itemKey, rowIndex) => {
    const owner = panelMapRef.current[itemKey];
    if (!owner) return;

    setPreData((prev) =>
      prev.map((itm) => {
        const isSameItem = itm.key === owner.key;
        if (!isSameItem) return itm;

        const newAsset = (itm.assetLand || []).map((assetLand, idx) =>
          idx === rowIndex
            ? { ...assetLand, active: !assetLand.active }
            : assetLand,
        );

        const hasAnyActive = newAsset.some((a) => a.active === true);

        return {
          ...itm,
          assetLand: newAsset,
          investigation: hasAnyActive,
        };
      }),
    );

    setChangedKeys((prev) => new Set([...prev, owner.key]));
    message.success("อัปเดตสถานะทรัพย์เรียบร้อย");
  };

  const handleCopyAsset = async (itemKey, rowIndex) => {
    const owner = panelMapRef.current[itemKey];
    if (!owner) return;

    const item = preData.find((itm) => itm.key === owner.key);
    if (!item) return;

    const assetToCopy = (item.assetLand || [])[rowIndex];
    if (!assetToCopy) {
      message.warning("ไม่พบข้อมูลทรัพย์ที่ต้องการคัดลอก");
      return;
    }

    // สร้างข้อความที่จะคัดลอก
    const copyText = `${
      assetToCopy.typeLand === 1
        ? "โฉนด"
        : assetToCopy.typeLand === 2
          ? "นส.3 ก."
          : assetToCopy.typeLand === 3
            ? "ห้องชุด"
            : assetToCopy.typeLand === 4
              ? "นภ.1"
              : "อื่น ๆ"
    } ${assetToCopy.numberLand || "-"} จ.${assetToCopy.provinceName || "-"} อ.${
      assetToCopy.districtName || "-"
    } ต.${assetToCopy.subdistrictName || "-"}`;
    console.log(item);

    try {
      await navigator.clipboard.writeText(copyText);
      message.success("คัดลอกข้อมูลทรัพย์เรียบร้อย");
    } catch (err) {
      console.error("Failed to copy:", err);
      message.error("ไม่สามารถคัดลอกข้อมูลได้");
    }
  };

  // ======================== Money Inputs ========================
  const MoneyInputs = ({ itemKey, money }) => {
    const [form] = Form.useForm();
    const [, setTotal] = useState(0);

    const toNum = (v) => {
      if (!v) return 0;
      const cleaned = String(v).replace(/,/g, "");
      return Number(cleaned) || 0;
    };

    const calculateAndUpdateTotal = () => {
      const values = form.getFieldsValue();
      const newTotal =
        toNum(values.tr14) +
        toNum(values.familyRegistration) +
        toNum(values.operationFee) +
        toNum(values.spouseFee) +
        toNum(values.additionalFee);

      setTotal(newTotal);
      form.setFieldsValue({ total: String(newTotal) });
      return newTotal;
    };

    const handleFieldChange = () => {
      const newTotal = calculateAndUpdateTotal();
      const owner = panelMapRef.current[itemKey];

      if (!owner) {
        console.warn("Owner not found for itemKey:", itemKey);
        return;
      }

      const values = form.getFieldsValue();
      const updatedMoney = {
        moneyOnlineId: money?.moneyOnlineId || 0,
        assetOnlineId: owner.assetOnlineId || 0,
        tr14: String(toNum(values.tr14)),
        familyRegistration: String(toNum(values.familyRegistration)),
        operationFee: String(toNum(values.operationFee)),
        spouseFee: String(toNum(values.spouseFee)),
        additionalFee: String(toNum(values.additionalFee)),
        total: String(newTotal),
        payStatus: money?.payStatus || null,
      };

      setPreData((prev) =>
        prev.map((itm) => {
          const isSameItem = itm.key === owner.key;
          if (!isSameItem) return itm;

          return { ...itm, money: updatedMoney };
        }),
      );
      setChangedKeys((prev) => new Set([...prev, owner.key]));
    };

    useEffect(() => {
      if (money) {
        calculateAndUpdateTotal();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [money]);

    return (
      <Form
        form={form}
        initialValues={{
          tr14: money?.tr14 ?? "",
          familyRegistration: money?.familyRegistration ?? "",
          operationFee: money?.operationFee ?? "",
          spouseFee: money?.spouseFee ?? "",
          additionalFee: money?.additionalFee ?? "",
          total: money?.total ?? "0",
        }}
        layout="vertical"
      >
        <div
          style={{
            background: "#f8f9fa",
            padding: "24px",
            borderRadius: "12px",
            border: "1px solid #e8e8e8",
          }}
        >
          <div
            style={{
              marginBottom: "16px",
              fontSize: "16px",
              fontWeight: 600,
              color: "#262626",
            }}
          >
            ค่าดำเนินการ
          </div>

          <Space direction="horizontal" size={16} wrap>
            <Form.Item
              label={
                <span style={{ fontSize: "13px", color: "#595959" }}>
                  คัด ทร.14
                </span>
              }
              name="tr14"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                disabled={money.payStatus}
                stringMode
                min={0}
                precision={0}
                inputMode="numeric"
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                onKeyDown={(e) => {
                  if (
                    !/[0-9]|Backspace|Delete|ArrowLeft|ArrowRight|Tab/.test(
                      e.key,
                    )
                  ) {
                    e.preventDefault();
                  }
                }}
                onChange={calculateAndUpdateTotal}
                onBlur={handleFieldChange}
                placeholder="0"
                style={{ width: 140 }}
              />
            </Form.Item>

            <Form.Item
              label={
                <span style={{ fontSize: "13px", color: "#595959" }}>
                  ทะเบียนครอบครัว
                </span>
              }
              name="familyRegistration"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                disabled={money.payStatus}
                stringMode
                min={0}
                precision={0}
                inputMode="numeric"
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                onKeyDown={(e) => {
                  if (
                    !/[0-9]|Backspace|Delete|ArrowLeft|ArrowRight|Tab/.test(
                      e.key,
                    )
                  ) {
                    e.preventDefault();
                  }
                }}
                onChange={calculateAndUpdateTotal}
                onBlur={handleFieldChange}
                placeholder="0"
                style={{ width: 140 }}
              />
            </Form.Item>

            <Form.Item
              label={
                <span style={{ fontSize: "13px", color: "#595959" }}>
                  ดำเนินงาน
                </span>
              }
              name="operationFee"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                disabled={money.payStatus}
                stringMode
                min={0}
                precision={0}
                inputMode="numeric"
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                onKeyDown={(e) => {
                  if (
                    !/[0-9]|Backspace|Delete|ArrowLeft|ArrowRight|Tab/.test(
                      e.key,
                    )
                  ) {
                    e.preventDefault();
                  }
                }}
                onChange={calculateAndUpdateTotal}
                onBlur={handleFieldChange}
                placeholder="0"
                style={{ width: 140 }}
              />
            </Form.Item>

            <Form.Item
              label={
                <span style={{ fontSize: "13px", color: "#595959" }}>
                  คู่สมรส
                </span>
              }
              name="spouseFee"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                disabled={money.payStatus}
                stringMode
                min={0}
                precision={0}
                inputMode="numeric"
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                onKeyDown={(e) => {
                  if (
                    !/[0-9]|Backspace|Delete|ArrowLeft|ArrowRight|Tab/.test(
                      e.key,
                    )
                  ) {
                    e.preventDefault();
                  }
                }}
                onChange={calculateAndUpdateTotal}
                onBlur={handleFieldChange}
                placeholder="0"
                style={{ width: 140 }}
              />
            </Form.Item>

            <Form.Item
              label={
                <span style={{ fontSize: "13px", color: "#595959" }}>
                  ตรวจเพิ่ม
                </span>
              }
              name="additionalFee"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                disabled={money.payStatus}
                stringMode
                min={0}
                precision={0}
                inputMode="numeric"
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                onKeyDown={(e) => {
                  if (
                    !/[0-9]|Backspace|Delete|ArrowLeft|ArrowRight|Tab/.test(
                      e.key,
                    )
                  ) {
                    e.preventDefault();
                  }
                }}
                onChange={calculateAndUpdateTotal}
                onBlur={handleFieldChange}
                placeholder="0"
                style={{ width: 140 }}
              />
            </Form.Item>

            <Divider
              type="vertical"
              style={{
                height: "auto",
                margin: "0 8px",
                borderColor: "#d9d9d9",
              }}
            />

            <Form.Item
              label={
                <span
                  style={{
                    fontSize: "13px",
                    color: "#1890ff",
                    fontWeight: 600,
                  }}
                >
                  รวมทั้งหมด
                </span>
              }
              name="total"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                stringMode
                min={0}
                precision={0}
                inputMode="numeric"
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                style={{ width: 160, fontWeight: 600, fontSize: "15px" }}
                disabled
              />
            </Form.Item>
          </Space>
        </div>
      </Form>
    );
  };

  // ======================== People ========================
  const handleAddPerson = (value) => {
    const newKey = generatePersonKey();

    const newPerson = {
      ...value,
      key: newKey,
      isNewPerson: true,
      assetLand:
        value.assetLand?.map((a) => ({
          ...a,
          key: a.key || generateAssetKey(),
        })) || [],
      money: value.money || {
        moneyOnlineId: null,
        assetOnlineId: null,
        tr14: "0",
        familyRegistration: "0",
        operationFee: "0",
        spouseFee: "0",
        additionalFee: "0",
        total: "0",
        payStatus: null,
      },
    };
    console.log("newPerson", newPerson);

    setPreData((prev) => [...prev, newPerson]);
    setChangedKeys((prev) => new Set([...prev, newKey]));
    message.success("เพิ่มคนใหม่สำเร็จ");
  };

  const handleDeletePerson = (itemKey) => {
    const owner = panelMapRef.current[itemKey];
    if (!owner) return;

    Modal.confirm({
      title: "ยืนยันการลบ",
      content: `ต้องการลบ ${fullName(owner)} ใช่หรือไม่?`,
      okText: "ยืนยัน",
      cancelText: "ยกเลิก",
      onOk: () => {
        setPreData((prev) => prev.filter((itm) => itm.key !== owner.key));
        message.success("ลบคนสำเร็จ");
      },
    });
  };

  // เช็คข้อมูล assetLand ของ item ที่ระบุ
  const checkAssetByIndex = (dataArr, itemIndex) => {
    const itemsToCheck =
      itemIndex !== undefined ? [dataArr[itemIndex]] : dataArr;

    const isValid = itemsToCheck.every((item) => {
      const activeAssets = (item.assetLand || []).filter(
        (a) => a.active === true,
      );
      return activeAssets.every(
        (a) =>
          !!a.province &&
          !!a.district &&
          a.numberLand &&
          a.numberLand.trim() !== "",
      );
    });

    const assetDetail = itemsToCheck.map((item, idx) => {
      const missingProvince = item.assetLand?.filter((a) => !a.province) || [];
      const missingDistrict = item.assetLand?.filter((a) => !a.district) || [];
      const missingNumberLand =
        item.assetLand?.filter(
          (a) => !a.numberLand || a.numberLand.trim() === "",
        ) || [];

      return {
        itemIndex: itemIndex !== undefined ? itemIndex : idx,
        data: item,
        totalAsset: item.assetLand?.length || 0,
        missingProvinceCount: missingProvince.length,
        missingDistrictCount: missingDistrict.length,
        missingNumberLandCount: missingNumberLand.length,
        isValid:
          item.assetLand &&
          item.assetLand.length > 0 &&
          item.assetLand.every(
            (a) =>
              a.province &&
              a.district &&
              a.active &&
              a.numberLand &&
              a.numberLand.trim() !== "",
          ),
      };
    });

    return { isValid, assetDetail };
  };

  // ======================== Save ========================
  const handleSave = async () => {
    console.log("preData", preData);
    console.log("memoData", memoData);

    try {
      setLoading(true);
      const { isValid, assetDetail } = checkAssetByIndex(preData);

      if (!isValid) {
        const invalidItems = assetDetail.filter((d) => !d.isValid);
        const errorList = invalidItems.map((d, index) => {
          if (
            d.missingProvinceCount === 0 &&
            d.missingDistrictCount === 0 &&
            d.missingNumberLandCount === 0
          ) {
            return null;
          }
          return (
            <li key={index}>
              ข้อมูล{" "}
              <b>
                {d.data.SNAM} {d.data.NAME1} {d.data.NAME2 || ""}
              </b>
              {d.missingNumberLandCount > 0 && (
                <div>
                  : ยังไม่ได้กรอก "เลขที่ดิน" {d.missingNumberLandCount} ตัว
                </div>
              )}
              {d.missingProvinceCount > 0 && (
                <div>
                  : ยังไม่ได้เลือก "จังหวัด" {d.missingProvinceCount} ตัว
                </div>
              )}
              {d.missingDistrictCount > 0 && (
                <div>: ยังไม่ได้เลือก "อำเภอ" {d.missingDistrictCount} ตัว</div>
              )}
            </li>
          );
        });

        const checkErrors = errorList.every((item) => !item);

        if (!checkErrors) {
          notification.error({
            message: "ข้อมูลไม่ครบ",
            description: (
              <div>
                <p>กรุณาตรวจสอบข้อมูลต่อไปนี้:</p>
                <ul style={{ marginLeft: 20, paddingLeft: 10 }}>{errorList}</ul>
              </div>
            ),
            duration: 8,
            placement: "topRight",
          });
          return;
        }
      }

      message.success("ข้อมูลครบถ้วน พร้อมส่ง");
      await postData(preData);

      if (userCompany !== "3") {
        await postMemo();
      }

      if (namePage === "import") {
        func?.(true, preData[0].invRoundId);
      } else {
        func?.(true, "success");
      }
    } catch (e) {
      console.error("handleSave error:", e);
      message.error("เกิดข้อผิดพลาดในการบันทึก");
    } finally {
      setLoading(false);
    }
  };

  const postData = async (rawData) => {
    try {
      const res = await axios.post(
        BASE_URL_BEN + POST_EDIT_ASSET,
        { data: rawData },
        { headers: HEADERS_EXPORT_BEN },
      );
      message.success("บันทึกสำเร็จ");
      close?.(false);
      return res.data;
    } catch (error) {
      message.error("เกิดข้อผิดพลาด");
      throw error;
    }
  };
  console.log(memoData);

  const postMemo = async () => {
    setLoading(true);
    const payload = {
      DATA_TYPE: memoData.DATA_TYPE,
      CONTNO: memoData.CONTNO,
      MEMO1: memoData.MEMO1,
    };

    try {
      const res = await axios.post(POST_UPDATE_MEMO, payload, {
        headers: HEADERS_EXPORT_BEN,
      });
      const { status, data } = res;
      if (status === 200) {
        message.success(`บันทึกหมายเหตุสำเร็จ`);
      }
      return data;
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message;

      if (status === 404) message.error("ไม่พบปลายทาง (404)");
      else if (status === 400) message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
      else if (status === 500)
        message.error("เคยนำข้อมูลเข้าแล้วหรือข้อมูลบัตรซ้ำกัน !");
      else
        message.error(
          `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
        );

      console.error("postData error:", error);
      throw error;
    }
  };

  // ======================== Items ========================
  const items = preData.map((item, idx) => {
    const itemKey = item.key; // ใช้ key เป็นหลัก
    const isInvalid = item.customerId === 0; // ใช้ flag เดิมเพื่อ UI ได้ (ไม่ใช้เป็นคีย์)
    const isChanged = changedKeys.has(itemKey);
    const { isValid: isAssetValid } = checkAssetByIndex(preData, idx);

    const selectOptions = data.map((v) => ({
      label: `${fullName(v)} (${v.contno})`,
      value: v.customerId, // mapping กับ relation ตาม customerId เดิม (สำหรับ backend)
      data: v,
    }));

    const picked = selectedMap[itemKey];

    return {
      key: itemKey,
      label: (
        <div onClick={() => toggleItem(itemKey)} style={{ cursor: "pointer" }}>
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Space direction="vertical" size={5}>
              {isChanged && (
                <Tag
                  color={isAssetValid ? "green" : "red"}
                  style={{ fontSize: 12 }}
                >
                  <strong>✏️ มีการแก้ไขทรัพย์</strong>
                </Tag>
              )}

              <Tag color={isInvalid ? "red" : "green"}>
                <strong>{item.contno}</strong>
              </Tag>
              <div>{fullName(item)}</div>
              <div style={{ fontSize: 12, color: "#666" }}>
                เลขบัตร: {item.CUSCOD || "-"}
              </div>
            </Space>

            <Space direction="vertical" size={0} align="end">
              <Tag
                color={item.assetLand.length > 0 ? "green" : "orange"}
                style={{ fontSize: 12 }}
              >
                {item.assetLand.length > 0 ? "✓ พบทรัพย์" : "✗ ไม่พบทรัพย์"}
              </Tag>

              {item.relationCustomerId && (
                <>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    popupMatchSelectWidth={false}
                    placeholder="เลือกบุคคลอ้างอิง..."
                    style={{ width: 260, marginTop: 8 }}
                    options={selectOptions}
                    value={
                      picked?.customerId ||
                      data.find((v) => v.customerId === item.relationCustomerId)
                        ?.customerId ||
                      null
                    }
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(value) => {
                      const selectedData = selectOptions.find(
                        (opt) => opt.value === value,
                      )?.data;

                      setPreData((prev) =>
                        prev.map((itm) =>
                          itm.key === item.key
                            ? {
                                ...itm,
                                relationCustomerId: value, // เก็บรหัสเดิมเพื่อ backend
                                relation: itm.relation || "",
                              }
                            : itm,
                        ),
                      );

                      setSelectedMap((prev) => ({
                        ...prev,
                        [itemKey]: {
                          customerId: value, // อิง customerId สำหรับ relation
                          data: selectedData,
                          relation: picked?.relation || item.relation || "",
                        },
                      }));

                      setChangedKeysRelation(
                        (prev) => new Set([...prev, itemKey]),
                      );
                    }}
                  />

                  <Input
                    placeholder="ความสัมพันธ์ (เช่น บุตร, คู่สมรส)"
                    style={{ width: 260, marginTop: 8 }}
                    value={picked?.relation || item.relation || ""}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      setPreData((prev) =>
                        prev.map((itm) =>
                          itm.key === item.key
                            ? { ...itm, relation: e.target.value }
                            : itm,
                        ),
                      );

                      setSelectedMap((prev) => ({
                        ...prev,
                        [itemKey]: {
                          ...prev[itemKey],
                          relation: e.target.value,
                        },
                      }));

                      setChangedKeysRelation(
                        (prev) => new Set([...prev, itemKey]),
                      );
                    }}
                  />
                </>
              )}

              {!item.customerId || item.isNewPerson ? (
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  style={{ marginTop: 8 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeletePerson(itemKey);
                  }}
                >
                  ลบคน
                </Button>
              ) : null}
            </Space>
          </Space>
        </div>
      ),
      children: (
        <>
          <Card title="ทรัพย์สิน" size="small" style={{ marginBottom: 8 }}>
            <Table
              dataSource={item.assetLand}
              columns={makeAssetColumns(itemKey)}
              rowKey={(row) => row.key} // ใช้ key ของ assetLand เสมอ
              pagination={false}
              size="small"
              bordered
              style={{ marginTop: 8 }}
              rowClassName={(record) =>
                record.active === false ? "row-inactive" : ""
              }
            />
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => handleAddAsset(itemKey)}
              style={{ marginTop: 8, width: "100%" }}
            >
              เพิ่มทรัพย์
            </Button>
          </Card>

          <Card title="ค่าดำเนินการ" size="small">
            <MoneyInputs itemKey={itemKey} money={item.money} />
          </Card>
        </>
      ),
    };
  });

  // ======================== Render ========================
  return (
    <Modal
      title="✏️ แก้ไขข้อมูลสืบทรัพย์"
      open={open}
      onCancel={() => close?.(false)}
      width={1200}
      centered
      keyboard={true}
      maskClosable={false}
      footer={[
        <div
          key="footer-container"
          style={{ display: "flex", flexDirection: "column", gap: "12px" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 16px",
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              fontSize: "13px",
              border: "1px solid #f0f0f0",
            }}
          >
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              <div>
                ทร.14:{" "}
                <b style={{ color: "#1890ff" }}>
                  {sumMoney.tr14Sum?.toLocaleString() || 0}
                </b>
              </div>
              <div>
                ทะเบียนครอบครัว:{" "}
                <b style={{ color: "#1890ff" }}>
                  {sumMoney.familyRegistrationSum?.toLocaleString() || 0}
                </b>
              </div>
              <div>
                ค่าดำเนินงาน:{" "}
                <b style={{ color: "#1890ff" }}>
                  {sumMoney.operationFeeSum?.toLocaleString() || 0}
                </b>
              </div>
              <div>
                ค่าตรวจเพิ่ม:{" "}
                <b style={{ color: "#1890ff" }}>
                  {sumMoney.additionalFeeSum?.toLocaleString() || 0}
                </b>
              </div>
              <div
                style={{ paddingLeft: "12px", borderLeft: "2px solid #d9d9d9" }}
              >
                รวม:{" "}
                <b style={{ color: "#1890ff", fontSize: "15px" }}>
                  {sumMoney.totalSum?.toLocaleString() || 0}
                </b>{" "}
                บาท
              </div>
            </div>
          </div>
          {/* ส่วนที่ 2: ปุ่มกด (Action Buttons) */}
          <div
            style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
          >
            <Button
              key="add"
              type="dashed"
              icon={<UserAddOutlined />}
              onClick={() => setIsModalAddCustomerRelation(true)}
            >
              เพิ่มคน
            </Button>
            <Button key="close" onClick={() => close?.(false)}>
              ปิด
            </Button>

            <Button
              key="save"
              type="primary"
              loading={loading}
              onClick={handleSave}
            >
              บันทึก
            </Button>
          </div>
        </div>,
      ]}
    >
      <div
        ref={scrollRef}
        style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: 8 }}
      >
        <Spin spinning={loading}>
          {preData.length > 0 ? (
            <>
              <Collapse
                items={items}
                activeKey={activeKey}
                onChange={setActiveKey}
                accordion
              />
              <Divider>หมายเหตุ</Divider>

              <div>
                <p style={{ color: "red" }}>
                  ***กรณีเพิ่ม/แก้ไข โปรดแก้ไขข้อมูลในช่องหมายเหตุด้วย
                </p>
                <Input.TextArea
                  value={memoData?.MEMO1}
                  onChange={(e) =>
                    setMemoData({ ...memoData, MEMO1: e.target.value })
                  }
                  rows={15}
                  placeholder="กรอกหมายเหตุ"
                />
              </div>
            </>
          ) : (
            <Empty description="ไม่มีข้อมูล" />
          )}
        </Spin>
        <FloatButton.BackTop target={() => scrollRef.current} />
      </div>

      {isModalAddCustomerRelation && (
        <AddCustomerRelation
          open={isModalAddCustomerRelation}
          close={setIsModalAddCustomerRelation}
          data={preData}
          funcDataBack={handleAddPerson}
        />
      )}
    </Modal>
  );
};

export default EditRelationAssets;
